/* eslint-disable unicorn/prefer-global-this */
'use client';

import { useEffect } from 'react';

import { DEBUG_NAMESPACES } from '@/libs/debug/debug-namespaces';
import {
  applyDebugSearchParams,
  disableDebug,
  enableDebug,
  getDebugLevel,
  getDebugSpec,
  isRuntimeOverrideAllowed,
  resetDebug,
} from '@/libs/debug/debug-settings';

/**
 * Wires up the runtime (no-rebuild) half of the debug switch.
 *
 * Mounted once in the root layout. Where `NEXT_PUBLIC_DEBUG_RUNTIME` is set it:
 *  - applies a `?debug=<spec>` URL override and persists it for this browser;
 *  - exposes `window.roomsterDebug` so debug can be toggled straight from the
 *    devtools console on a deployed build.
 *
 * Production ships without that flag, so neither affordance is installed there:
 * `window.roomsterDebug` is absent and `?debug=` is inert.
 *
 * Rendering nothing and doing all work in an effect keeps this out of SSR — the
 * override is intentionally per-browser, not per-deployment.
 */
export default function DebugBootstrap() {
  useEffect(() => {
    // Off in production: no globals installed, no URL parsing, no storage write.
    if (!isRuntimeOverrideAllowed()) {
      return;
    }

    // This mounts in the root layout, so it runs on every page for every user.
    // It provides no user-facing value, and an uncaught throw here would reach
    // the segment error boundary and blank the page — so nothing inside is
    // allowed to escape, however safe each call looks in isolation.
    try {
      installDebugGlobals();
    } catch {
      // A debug affordance must never be able to take a page down.
    }
  }, []);

  return null;
}

function installDebugGlobals(): void {
  const changedByUrl = applyDebugSearchParams(window.location.search);

  window.roomsterDebug = {
    namespaces: DEBUG_NAMESPACES,
    disable: () => {
      disableDebug();
      return 'debug disabled for this browser (reload not required)';
    },
    enable: (spec = '*', level) => {
      enableDebug(spec, level);
      return `debug enabled for "${spec}" at level "${getDebugLevel()}"`;
    },
    reset: () => {
      resetDebug();
      return 'runtime override cleared — build-time NEXT_PUBLIC_DEBUG applies again';
    },
    status: () => ({
      buildTimeSpec: process.env.NEXT_PUBLIC_DEBUG ?? '',
      level: getDebugLevel(),
      spec: getDebugSpec(),
    }),
  };

  const spec = getDebugSpec();

  if (spec) {
    console.info(
      `%c[debug]%c active — spec "${spec}", level "${getDebugLevel()}"${changedByUrl ? ' (set from ?debug=)' : ''}. `
      + 'Use window.roomsterDebug.enable("*") / .disable() to change it.',
      'color:#059669;font-weight:700',
      '',
    );
  }
}
